<template>
  <v-dialog :value="value" max-width="560" @input="$emit('input', $event)">
    <v-card class="share-note">
      <v-card-title class="share-note__title">
        {{ title || $t("shareNote.title") }}
      </v-card-title>

      <v-card-text>
        <p class="share-note__hint">
          {{ $t("shareNote.hint") }}
        </p>

        <v-select
          v-model="selectedGroupId"
          :items="groupOptions"
          item-text="label"
          item-value="id"
          :label="$t('shareNote.groupStudents')"
          outlined
          dense
          hide-details="auto"
          :no-data-text="$t('shareNote.noGroups')"
          @change="onGroupChange"
        />

        <div v-if="selectedGroupId" class="share-note__students">
          <div class="share-note__students-head">
            <span>{{ $t("shareNote.groupStudents") }}</span>
            <button
              v-if="students.length"
              type="button"
              class="share-note__toggle"
              @click="toggleAll"
            >
              {{
                allSelected
                  ? $t("shareNote.clearSelection")
                  : $t("shareNote.selectAll")
              }}
            </button>
          </div>

          <div v-if="!students.length" class="share-note__empty">
            {{ $t("shareNote.noStudents") }}
          </div>

          <v-checkbox
            v-for="student in students"
            :key="student.id"
            v-model="selectedCustomerIds"
            :value="student.id"
            :label="student.name"
            dense
            hide-details
            class="share-note__check"
          />
        </div>

        <p v-if="alreadySharedWith.length" class="share-note__note">
          {{
            $tc("shareNote.alreadyShared", alreadySharedWith.length, {
              count: alreadySharedWith.length,
            })
          }}
        </p>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="busy" @click="$emit('input', false)">
          {{ $t("common.cancel") }}
        </v-btn>
        <v-btn
          color="primary"
          depressed
          :loading="busy"
          :disabled="!canConfirm"
          @click="confirm"
        >
          {{ confirmLabel || $t("shareNote.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { groupLabelByStudents } from "@/shared/classPresentation";

export default {
  name: "ShareNoteDialog",

  props: {
    value: { type: Boolean, default: false },
    // Turmas do professor, no formato devolvido por GET /partners/groups:
    // { id, cod, level, content: [{ id, name }] }
    groups: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
    title: { type: String, default: "" },
    confirmLabel: { type: String, default: "" },
    // Alunos que já recebem esta anotação, para pré-marcar a seleção
    alreadySharedWith: { type: Array, default: () => [] },
  },

  data() {
    return {
      selectedGroupId: "",
      selectedCustomerIds: [],
    };
  },

  computed: {
    // O professor nao correlaciona codigo de turma com aluno nenhum -- ele
    // pensa "vou compartilhar com a Ana e o Bruno". A turma continua sendo o
    // recorte, mas quem nomeia a opcao sao os alunos dela.
    groupOptions() {
      return this.groups.map((group) => ({
        id: group.id,
        label: groupLabelByStudents(group),
      }));
    },
    selectedGroup() {
      return this.groups.find((group) => group.id === this.selectedGroupId);
    },
    students() {
      return (this.selectedGroup && this.selectedGroup.content) || [];
    },
    allSelected() {
      return (
        this.students.length > 0 &&
        this.students.every((student) =>
          this.selectedCustomerIds.includes(student.id)
        )
      );
    },
    canConfirm() {
      return !!this.selectedGroupId && this.selectedCustomerIds.length > 0;
    },
  },

  watch: {
    value(opened) {
      if (opened) this.reset();
    },
  },

  methods: {
    reset() {
      this.selectedGroupId = this.groups.length === 1 ? this.groups[0].id : "";
      this.preselect();
    },
    // Deixa marcado quem já recebeu a anotação, para o professor enxergar o
    // estado atual em vez de partir do zero.
    preselect() {
      const ids = this.students.map((student) => student.id);
      this.selectedCustomerIds = this.alreadySharedWith.filter((customerId) =>
        ids.includes(customerId)
      );
    },
    onGroupChange() {
      this.preselect();
    },
    toggleAll() {
      this.selectedCustomerIds = this.allSelected
        ? []
        : this.students.map((student) => student.id);
    },
    confirm() {
      if (!this.canConfirm) return;

      this.$emit("confirm", {
        classId: this.selectedGroupId,
        customerIds: [...this.selectedCustomerIds],
      });
    },
  },
};
</script>

<style scoped>
.share-note__title {
  font-family: "Poppins", sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
}

.share-note__hint {
  color: rgba(31, 41, 55, 0.68);
  margin-bottom: 1rem;
}

.share-note__students {
  margin-top: 1.25rem;
}

.share-note__students-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: var(--dk-ink, #1f2937);
  margin-bottom: 0.35rem;
}

.share-note__toggle {
  background: none;
  border: none;
  color: var(--dk-pink, #db2777);
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.share-note__check {
  margin-top: 0;
}

.share-note__empty {
  color: rgba(31, 41, 55, 0.58);
  padding: 0.75rem 0;
}

.share-note__note {
  margin-top: 1rem;
  margin-bottom: 0;
  font-size: 0.85rem;
  color: rgba(31, 41, 55, 0.6);
}
</style>
